import React, { useRef, useEffect } from "react";
import { SharePointFile } from "../../interfaces/sharePointTypes";
import WordIcon from "../../../assets/Word.svg";
import PDFIcon from "../../../assets/PDF.svg";

interface DocumentSuggestionsProps {
  suggestions: SharePointFile[];
  selectedIndex: number;
  onSelect: (document: SharePointFile) => void;
  position: { top: number; left: number };
}

export const DocumentSuggestions: React.FC<DocumentSuggestionsProps> = ({
  suggestions,
  selectedIndex,
  onSelect,
  position,
}) => {
  // Refs to track each suggestion item for auto-scrolling
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Auto-scroll selected item into view when selectedIndex changes
  useEffect(() => {
    const selectedElement = itemRefs.current[selectedIndex];
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);
  const formatFileSize = (bytes: number): string => {
    const kb = bytes / 1024;
    return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getFileIcon = (file: SharePointFile) => {
    // Check MIME type first
    if (file.mimeType === "application/pdf") {
      return PDFIcon;
    }

    // Fallback to file extension
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "pdf") {
      return PDFIcon;
    }

    // Default to Word icon for .docx and other files
    return WordIcon;
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: position.top,
        left: position.left,
        background: "white",
        border: "1px solid #e0e0e0",
        borderRadius: "6px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        maxWidth: "320px",
        zIndex: 1000,
        fontSize: "13px",
        fontFamily: "'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column",
        maxHeight: "240px",
      }}
    >
      {/* Scrollable suggestions area */}
      <div
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: "1 1 auto",
        }}
      >
        {suggestions.map((doc, index) => (
          <div
            key={doc.id}
            ref={(el) => (itemRefs.current[index] = el)}
            onClick={() => onSelect(doc)}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f7fa")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                index === selectedIndex ? "#f5f7fa" : "transparent")
            }
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "8px 12px",
              cursor: "pointer",
              backgroundColor: index === selectedIndex ? "#f5f7fa" : "transparent",
              transition: "background 0.1s",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {(() => {
                const Icon = getFileIcon(doc);
                return <Icon style={{ maxWidth: "100%", maxHeight: "100%", height: "auto" }} />;
              })()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {doc.name}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#666",
                  display: "flex",
                  gap: "8px",
                }}
              >
                <span>{formatDate(doc.modifiedAt ?? doc.createdAt)}</span>
                <span>•</span>
                <span>{formatFileSize(doc.size ?? 0)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Keyboard hints footer - always visible, no scrolling needed */}
      {suggestions.length > 0 && (
        <div
          style={{
            padding: "6px 12px",
            borderTop: "1px solid #e5e7eb",
            fontSize: "10px",
            color: "#9ca3af",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
            backgroundColor: "#fafbfc",
            fontFamily: "'Segoe UI', sans-serif",
            flexShrink: 0,
          }}
        >
          <span>↑↓ Navigate</span>
          <span>↵ Select</span>
          <span>Esc Close</span>
        </div>
      )}
    </div>
  );
};
